import { SourcingHiringGoal, SourcingStats } from 'api-clients/monolith';
import { Info, Loader2 } from 'lucide-react';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { CloseIcon } from '@/components/icons/close';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Typography } from '@/components/ui/typography';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { useUpdateHiringGoalTargets } from '@/hooks/use-update-hiring-goal-targets';

interface ApplicantTargetPopoverProps {
  hiringGoal: SourcingHiringGoal | null;
  applicantsStats: SourcingStats;
  conversionStats: SourcingStats;
  isReadOnly?: boolean;
}

/**
 * Two-way applicants/conversion target editor for the Sourced This Period
 * card. Both fields derive from the same hiring-goal hire count, so editing
 * either recomputes the other; only `expected_conversion` is persisted, as
 * "target applicants" is just its inverse.
 */
export function ApplicantTargetPopover({
  hiringGoal,
  applicantsStats,
  conversionStats,
  isReadOnly = false,
}: ApplicantTargetPopoverProps) {
  const intl = useIntl();
  const openingSlug = useOpeningSlug();

  const [open, setOpen] = useState(false);
  const [applicants, setApplicants] = useState('');
  const [conversion, setConversion] = useState('');
  const [lastEdited, setLastEdited] = useState<'applicants' | 'conversion'>(
    'conversion',
  );

  const goal = hiringGoal?.goal ?? null;
  const hasTarget =
    applicantsStats.expected != null && conversionStats.expected != null;

  const resetFromStats = useCallback(() => {
    setApplicants(
      applicantsStats.expected ? String(applicantsStats.expected) : '',
    );
    setConversion(
      conversionStats.expected ? String(conversionStats.expected) : '',
    );
    setLastEdited('conversion');
  }, [applicantsStats.expected, conversionStats.expected]);

  useEffect(() => {
    resetFromStats();
  }, [resetFromStats]);

  // Discard any unsaved edits from a previous open — always start from the
  // latest API-derived values rather than whatever was left in state.
  const handleOpenChange = (next: boolean) => {
    if (next) resetFromStats();
    setOpen(next);
  };

  const handleApplicantsChange = (value: string) => {
    setApplicants(value);
    setLastEdited('applicants');
    if (value.trim() === '') {
      setConversion('');
      return;
    }
    const numApplicants = parseFloat(value);
    if (goal && numApplicants > 0) {
      setConversion(
        String(
          Math.round(((goal / numApplicants) * 100 + Number.EPSILON) * 10) / 10,
        ),
      );
    }
  };

  const handleConversionChange = (value: string) => {
    setConversion(value);
    setLastEdited('conversion');
    if (value.trim() === '') {
      setApplicants('');
      return;
    }
    const numConversion = parseFloat(value);
    if (goal && numConversion > 0) {
      setApplicants(String(Math.round((goal * 100) / numConversion)));
    }
  };

  const { mutate: saveTarget, isPending } = useUpdateHiringGoalTargets({
    successMessage: intl.formatMessage({
      defaultMessage: 'Target updated successfully!',
      description: 'Success toast after saving the applicant target',
    }),
    errorMessage: intl.formatMessage({
      defaultMessage: 'Failed to update the target!',
      description: 'Error toast when saving the applicant target fails',
    }),
    onSuccess: () => setOpen(false),
  });

  const handleSave = () => {
    const numConversion = parseFloat(conversion);

    saveTarget({
      openingSlug,
      requestBody: { expected_conversion: numConversion },
    });
  };

  if (isReadOnly) return null;

  const numApplicants = parseFloat(applicants);
  const numConversion = parseFloat(conversion);
  const hasBothValues =
    !!numApplicants &&
    numApplicants > 0 &&
    !!numConversion &&
    numConversion > 0;
  const sourced = applicantsStats.actual ?? 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="link" size="link">
          {hasTarget ? (
            <FormattedMessage
              defaultMessage="Edit target"
              description="Trigger: edit the existing applicant/conversion target for this opening"
            />
          ) : (
            <FormattedMessage
              defaultMessage="+ Add target"
              description="Trigger: set an applicant/conversion target for this opening"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="border-fx-neutral-200 flex w-[345px] flex-col gap-2 rounded-lg p-3 shadow-lg"
      >
        <div className="flex w-full items-center justify-between">
          <Typography variant="subtitle2" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Applicant target"
              description="Popover title: setting applicant/conversion targets"
            />
          </Typography>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={intl.formatMessage({
              defaultMessage: 'Close',
              description: 'Close button: dismiss the applicant target popover',
            })}
            className="text-fx-neutral-400 hover:text-fx-neutral-600 shrink-0"
          >
            <CloseIcon className="size-4" />
          </button>
        </div>

        <div className="border-fx-neutral-200 w-full border-b" />

        <div className="flex w-full flex-col gap-3">
          <Input
            id="applicant-target-applicants"
            isNumber
            description={intl.formatMessage({
              defaultMessage: 'Applicants needed',
              description: 'Input label: target applicant count',
            })}
            placeholderLabel="#"
            placeholder={intl.formatMessage({
              defaultMessage: 'e.g. 250',
              description: 'Placeholder: example applicant count',
            })}
            value={applicants}
            onChange={e => handleApplicantsChange(e.target.value)}
          />
          <Input
            id="applicant-target-conversion"
            isNumber
            description={intl.formatMessage({
              defaultMessage: 'Expected conversion',
              description: 'Input label: target applicant-to-hire percent',
            })}
            placeholderLabel="%"
            placeholder={intl.formatMessage({
              defaultMessage: 'e.g. 10',
              description: 'Placeholder: example conversion percent',
            })}
            value={conversion}
            onChange={e => handleConversionChange(e.target.value)}
          />

          <div className="bg-fx-neutral-100 flex w-full gap-2 rounded-lg p-2">
            <Info className="text-fx-neutral-400 mt-0.5 size-4 shrink-0" />
            <Typography variant="small1" className="!text-fx-neutral-600">
              {!goal ? null : !hasBothValues ? (
                <FormattedMessage
                  defaultMessage="Enter either number to set the target — they describe the same goal of <b>{goal} hires</b>. Currently <b>{sourced} sourced</b>."
                  description="Info line: default guidance shown before either field has a value"
                  values={{
                    goal,
                    sourced,
                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                  }}
                />
              ) : lastEdited === 'applicants' ? (
                <FormattedMessage
                  defaultMessage="With a target of <b>{applicants} applicants</b>, the expected conversion to hire would be at <b>{conversion}%</b> (based on the goal set for {goal} hires)."
                  description="Info line: computed conversion from an applicants target"
                  values={{
                    applicants: numApplicants,
                    conversion: numConversion,
                    goal,
                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                  }}
                />
              ) : (
                <FormattedMessage
                  defaultMessage="At <b>{conversion}%</b> expected conversion, <b>{applicants} applicants</b> are needed to hit your goal of {goal} hires."
                  description="Info line: computed applicants from a conversion target"
                  values={{
                    applicants: numApplicants,
                    conversion: numConversion,
                    goal,
                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                  }}
                />
              )}
            </Typography>
          </div>

          <div className="flex w-full items-center justify-between">
            <Button
              variant="secondary"
              className="h-10"
              onClick={() => setOpen(false)}
            >
              <FormattedMessage
                defaultMessage="Cancel"
                description="Cancel button: dismiss the applicant target popover"
              />
            </Button>
            <Button className="h-10" onClick={handleSave} disabled={isPending}>
              {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              <FormattedMessage
                defaultMessage="Save"
                description="Save button: persist the applicant target"
              />
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
